<section class="alert-form">
  <div class="alert-form__header-row">
    <p class="alert-form__breadcrumb">
      Alerts · {{ isEditMode ? 'Edit' : 'New' }}<span class="sgl-arrows" aria-hidden="true"></span>
    </p>
    @if (isEditMode && target) {
      <span class="alert-form__editing-badge">
        <mat-icon class="material-symbols-outlined">edit</mat-icon>
        Editing #{{ target.alert.id }}
      </span>
    }
  </div>
  <h2 class="alert-form__heading">{{ isEditMode ? 'Edit alert' : 'New alert' }}</h2>
  <p class="alert-form__description">
    Shown to every mySGL user as a notification that stays on screen until they close it — never blocking what they're
    doing.
  </p>

  <div class="alert-form__divider"></div>

  <form class="alert-form__form" [formGroup]="alertForm">
    <div class="alert-form__field">
      <span class="m-label">Alert level <span class="alert-form__req">*</span></span>
      <div class="alert-form__level-grid">
        @for (level of levels; track level) {
          <button
            type="button"
            class="alert-form__level-tile"
            [class.alert-form__level-tile--selected]="alertForm.controls.level.value === level"
            [style.--tile-color]="levelMeta(level).color"
            (click)="selectLevel(level)">
            <mat-icon class="material-symbols-outlined" [style.color]="levelMeta(level).color">{{
              levelMeta(level).icon
            }}</mat-icon>
            <span>{{ levelMeta(level).label }}</span>
          </button>
        }
      </div>
      @if (levelError) {
        <p class="alert-form__error">{{ levelError }}</p>
      }
    </div>

    <div class="alert-form__field">
      <label class="m-label" for="alert-message">Message to the user <span class="alert-form__req">*</span></label>
      <textarea
        id="alert-message"
        class="m-input alert-form__textarea"
        rows="3"
        [maxlength]="messageMaxLength"
        formControlName="message"
        [placeholder]="
          (isMobile$ | async)
            ? 'What\'s happening and what to do — plain language...'
            : 'What\'s happening, what it means for you, and what to do — plain language, no jargon...'
        "></textarea>
      <p class="alert-form__hint">One or two sentences: what's happening and what you'd like the user to do.</p>
      @if (messageError) {
        <p class="alert-form__error">{{ messageError }}</p>
      }
    </div>

    <!-- Mobile shows one consolidated "Link" label above the (already-stacked) pair instead of
         each field's own label — see alert-form.component.scss's mobile media query, which hides
         this on desktop and hides the two per-field labels on mobile. -->
    <span class="m-label alert-form__link-label--mobile">Link</span>
    <div class="alert-form__row">
      <div class="alert-form__field">
        <label class="m-label alert-form__link-label--desktop" for="alert-link-text">Link text</label>
        <input id="alert-link-text" class="m-input" type="text" formControlName="linkText" placeholder="Read more" />
        <p class="alert-form__hint">Optional — shown under the message as a forward-arrow link.</p>
      </div>
      <div class="alert-form__field">
        <label class="m-label alert-form__link-label--desktop" for="alert-link-url">Link URL</label>
        <input id="alert-link-url" class="m-input" type="text" formControlName="linkUrl" placeholder="https://" />
        <p class="alert-form__hint">Optional — leave both blank for a message-only alert.</p>
      </div>
    </div>
    @if (linkError) {
      <p class="alert-form__error">{{ linkError }}</p>
    }

    <div class="alert-form__field">
      <span class="m-label">Visibility</span>
      <div class="alert-form__toggle-group">
        <button
          type="button"
          class="alert-form__toggle"
          [class.alert-form__toggle--selected]="alertForm.controls.visibilityMode.value === 'Scheduled'"
          (click)="selectVisibilityMode('Scheduled')">
          <mat-icon class="material-symbols-outlined">schedule</mat-icon>
          Scheduled
        </button>
        <button
          type="button"
          class="alert-form__toggle"
          [class.alert-form__toggle--selected]="alertForm.controls.visibilityMode.value === 'Manual'"
          (click)="selectVisibilityMode('Manual')">
          <mat-icon class="material-symbols-outlined">pan_tool</mat-icon>
          Manual
        </button>
      </div>
      <p class="alert-form__hint">
        @if (alertForm.controls.visibilityMode.value === 'Scheduled') {
          Shows and hides automatically between the two times, as long as the alert is active.
        } @else {
          Shows whenever active, until switched off — no schedule.
        }
      </p>
    </div>

    @if (alertForm.controls.visibilityMode.value === 'Scheduled') {
      <div class="alert-form__row">
        <div class="alert-form__field">
          <label class="m-label" for="alert-show-from">Show from <span class="alert-form__req">*</span></label>
          <mat-form-field class="alert-form__dt-field" appearance="outline" subscriptSizing="dynamic">
            <input
              id="alert-show-from"
              matInput
              [ngModel]="showFromDate"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onShowFromDateChange($event)"
              [ngxMatDatetimePicker]="showFromPicker"
              [min]="minScheduleDateTimeAsDate" />
            <mat-datepicker-toggle matSuffix [for]="$any(showFromPicker)" />
            <ngx-mat-datetime-picker #showFromPicker [stepHour]="1" [stepMinute]="1">
              <ngx-mat-datepicker-actions>
                <button type="button" mat-stroked-button ngxMatDatepickerApply>
                  <span class="material-symbols-outlined">check</span>
                </button>
              </ngx-mat-datepicker-actions>
            </ngx-mat-datetime-picker>
          </mat-form-field>
        </div>
        <div class="alert-form__field">
          <label class="m-label" for="alert-show-until">Show until <span class="alert-form__req">*</span></label>
          <mat-form-field class="alert-form__dt-field" appearance="outline" subscriptSizing="dynamic">
            <input
              id="alert-show-until"
              matInput
              [ngModel]="showUntilDate"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="onShowUntilDateChange($event)"
              [ngxMatDatetimePicker]="showUntilPicker"
              [min]="minShowUntilAsDate" />
            <mat-datepicker-toggle matSuffix [for]="$any(showUntilPicker)" />
            <ngx-mat-datetime-picker #showUntilPicker [stepHour]="1" [stepMinute]="1">
              <ngx-mat-datepicker-actions>
                <button type="button" mat-stroked-button ngxMatDatepickerApply>
                  <span class="material-symbols-outlined">check</span>
                </button>
              </ngx-mat-datepicker-actions>
            </ngx-mat-datetime-picker>
          </mat-form-field>
          <p class="alert-form__hint">All times are in UTC.</p>
        </div>
      </div>
      @if (scheduleError) {
        <p class="alert-form__error">{{ scheduleError }}</p>
      }
    }

    <div class="alert-form__active">
      <label class="alert-toggle">
        <input type="checkbox" formControlName="isActive" class="alert-toggle__input" />
        <span class="alert-toggle__track"><span class="alert-toggle__thumb"></span></span>
      </label>
      <span class="alert-form__active-label">
        <strong>Active</strong>
        —
        {{
          alertForm.controls.visibilityMode.value === 'Scheduled'
            ? 'shows automatically inside the schedule window'
            : 'shows immediately, until switched off'
        }}
      </span>
    </div>
  </form>

  <div class="alert-form__footer">
    <p class="alert-form__hint alert-form__footer-hint">Write the message users will see.</p>
    <div class="alert-form__footer-actions">
      <button type="button" class="m-btn m-btn-secondary" (click)="clear()">Clear</button>
      <button type="button" class="m-btn m-btn-primary" [disabled]="saving" (click)="save()">
        {{ saving ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save changes' : 'Create alert' }}
        <span class="sgl-arrows" aria-hidden="true"></span>
      </button>
    </div>
  </div>
</section>
